/**
 * Measure / ruler tool state — a multi-point ruler. Each tap on the chart adds a
 * point; the layer reads distance and bearing for each leg and the running total.
 * Started from the menu (empty, ready) or from the long-press radial (seeded at
 * the pressed point). Cleared when the user finishes.
 */
import { create } from 'zustand';

interface MeasureState {
  /** The ruler points, [lon, lat], in tap order. */
  points: [number, number][];
  /** Begin a fresh ruler seeded at one point (the radial's press location). */
  start: (p: [number, number]) => void;
  /** Add a point (a chart tap while the measure tool is active). */
  addPoint: (p: [number, number]) => void;
  /** Move an existing point by index (dragging a ruler vertex on the chart). */
  movePoint: (index: number, p: [number, number]) => void;
  /** Replace the whole ruler, e.g. lifting a saved measurement back for editing. */
  setPoints: (points: [number, number][]) => void;
  /** Drop the last point. */
  undo: () => void;
  /** Clear the ruler entirely. */
  clear: () => void;
}

export const useMeasureStore = create<MeasureState>((set) => ({
  points: [],
  start: (p) => set({ points: [p] }),
  addPoint: (p) => set((s) => ({ points: [...s.points, p] })),
  movePoint: (index, p) =>
    set((s) => (index < 0 || index >= s.points.length ? s : { points: s.points.map((pt, i) => (i === index ? p : pt)) })),
  setPoints: (points) => set({ points: [...points] }),
  undo: () => set((s) => ({ points: s.points.slice(0, -1) })),
  clear: () => set({ points: [] }),
}));
